import { PageHeader, Form, Input, Select, Button, Space, message } from 'antd'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { getLeadDetails, updateLead } from '../../network/MiniCRMs'
import Loader from '../../components/Loader'
import Error from '../../components/Error'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'
import { ROLE_PERMISSIONS } from '../../constants/common'
import { useAuth } from '../../hooks/useAuth'

const EditMiniCRM = ({ history }) => {
  const { leadId } = useParams()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role]
    ? ROLE_PERMISSIONS[user.role]
    : []

  const fromDetail = history.location.state?.fromDetail || false

  const leadDetails = useQuery(
    [
      'getLeadDetails',
      {
        id: leadId,
      },
    ],
    getLeadDetails,
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        form.setFieldsValue({
          firstName: data?.firstName,
          lastName: data?.lastName,
          type: data?.type,
          note: data?.note,
        })
      },
    }
  )

  const updateLeadMutation = useMutation(
    (values) => updateLead(values, leadId),
    {
      onSuccess: () => {
        message.success('Lead updated successfully')
        queryClient.invalidateQueries(['getLeadDetails'])
        queryClient.invalidateQueries(['getLeads'])
        if (fromDetail) {
          const url = createUrlWithFiltersFromCurrentURL(`/mini-crm/${leadId}`)
          history.push(url)
        } else {
          const url = createUrlWithFiltersFromCurrentURL('/mini-crm')
          history.push(url)
        }
      },
      onError: () => {
        message.error('Failed to update lead')
      },
    }
  )

  const handleCancel = () => {
    if (fromDetail) {
      const url = createUrlWithFiltersFromCurrentURL(`/mini-crm/${leadId}`)
      history.push(url)
    } else {
      const url = createUrlWithFiltersFromCurrentURL('/mini-crm')
      history.push(url)
    }
  }

  const handleSubmit = async (values) => {
    try {
      await updateLeadMutation.mutateAsync(values)
    } catch (error) {
      console.error('Error updating lead:', error)
    }
  }

  const canEdit = permissions.includes('can-edit-leads')

  if (!canEdit) {
    if (fromDetail) {
      const url = createUrlWithFiltersFromCurrentURL(`/mini-crm/${leadId}`)
      history.push(url)
    } else {
      const url = createUrlWithFiltersFromCurrentURL('/mini-crm')
      history.push(url)
    }
    return null
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="Edit Lead"
        onBack={handleCancel}
        style={{ backgroundColor: '#fff' }}
      />
      {leadDetails.isError ? (
        <Error retry={() => leadDetails.refetch()} />
      ) : leadDetails.isLoading ? (
        <Loader />
      ) : (
        <div
          className="section-from-vertical"
          style={{
            width: '100%',
            padding: '24px',
            backgroundColor: '#f0f2f5',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <div
            style={{
              width: '100%',
              margin: '0 auto',
              backgroundColor: '#fff',
              padding: '24px',
              borderRadius: '4px',
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                firstName: leadDetails.data?.firstName,
                lastName: leadDetails.data?.lastName,
                type: leadDetails.data?.type,
                note: leadDetails.data?.note,
              }}
            >
              <Form.Item label="First Name" name="firstName">
                <Input placeholder="Enter first name" />
              </Form.Item>

              <Form.Item label="Last Name" name="lastName">
                <Input placeholder="Enter last name" />
              </Form.Item>

              <Form.Item label="Type" name="type">
                <Select placeholder="Select a type">
                  <Select.Option value="potential lead">
                    Potential Lead
                  </Select.Option>
                  <Select.Option value="qualified lead">
                    Qualified Lead
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item label="Note" name="note">
                <Input.TextArea rows={4} placeholder="Note for this lead" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateLeadMutation.isLoading}
                  >
                    Save
                  </Button>
                  <Button onClick={handleCancel}>Cancel</Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </div>
      )}
    </>
  )
}

export default EditMiniCRM
